// Copyright (c) Docugami, Inc. All rights reserved.

import { useEffect } from 'react';
import { DocumentApi } from 'networking/api';
import { FeatureFlags } from 'store/featureFlags/types';
import { reportErrorEncountered } from 'store/authentication/actions';
import { RequestState, useAsyncHelper } from 'hooks/AsyncHelper';
import { useAppDispatch } from 'hooks/redux';
import { useFeatureFlag } from 'hooks/FeatureFlags';
import { Telemetry } from 'telemetry/TelemetryManager';
import { DownloadXMLCommandSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { IDocumentCommand } from 'components/file_management/DocumentCommand';
import { FileManagerInstance, FileProperties } from 'filemanager/FileManager';
import { isDirectory } from 'components/file_management/file_helpers';

/**
 * Command used to download the associated DGML data for a given document if available
 */
export function useDownloadXmlCommand(items: string[], onComplete: (error?: any) => void): IDocumentCommand {
    const { execute, state, error } = useAsyncHelper<void>("Download XML");
    const { execute: ensureFile, state: ensureFileState } = useAsyncHelper("Ensure File", items);
    const downloadAllPipelineOutput = useFeatureFlag(FeatureFlags.DownloadAllPipelineOutput);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (state === RequestState.Complete || state === RequestState.Error) {
            if (state === RequestState.Error) {
                dispatch(reportErrorEncountered('downloadXmlAsync', error!));
            }

            onComplete(error);
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state, error]);

    useEffect(() => {
        if (items.length === 1 && !isDirectory(items[0]) && ensureFileState === RequestState.None) {
            ensureFile(FileManagerInstance.fetchFiles(items));
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ensureFile, ensureFileState])

    async function downloadXmlAsync(props: FileProperties) {
        const api = new DocumentApi();

        const response = await api.documentDownloadAllFiles(props.folderId!, { responseType: "blob" }, downloadAllPipelineOutput);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(response.data);
        const idx = props.name.lastIndexOf('.');
        a.download = `${idx === -1 ? props.name : props.name.substr(0, idx)}.zip`;
        a.click();
        URL.revokeObjectURL(a.href);
    }

    function downloadXmlClicked(): void {
        Telemetry.trackEvent<DownloadXMLCommandSchema>({
            eventName: TelemetryEvent.DownloadXMLCommand
        });
        const file = FileManagerInstance.getFile(items[0]);
        execute(downloadXmlAsync(file!.properties));
    }

    function isEnabled() {
        let isEnabled = false;
        if (ensureFileState === RequestState.Complete) {
            const file = FileManagerInstance.getFile(items[0]);
            isEnabled = !!file?.properties.hasCpml;
        }

        return isEnabled;
    }

    return {
        id: "Download XML Command",
        isActive: state === RequestState.InProgress,
        label: state === RequestState.InProgress ? "Downloading XML..." : "Download XML",
        isEnabled: isEnabled(),
        visual: null,
        execute: downloadXmlClicked
    }
}
